import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Carousel from 'react-material-ui-carousel';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaMapMarkerAlt } from 'react-icons/fa';
import { server } from "../../server";
import EventCard from './EventCard';
import CreateEventPrompt from './CreateEventPrompt'; // Import the fallback prompt
import { formatEventTimeRange } from './eventTimeDisplay';

const noop = () => {};

const formatEventDate = (event) => {
    if (!event?.date) {
        return 'Date pending';
    }

    const eventDate = new Date(event.date);
    return Number.isNaN(eventDate.getTime())
        ? 'Date pending'
        : eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const EventMobileRailCard = ({ event, isGoing, onCancelRSVP, onRSVP }) => {
    const formattedTime = formatEventTimeRange(event);

    const handleAttendanceClick = () => {
        if (isGoing) {
            onCancelRSVP(event._id);
            return;
        }

        onRSVP(event._id);
    };

    return (
        <article className="events-carousel-mobile-card">
            <Link to={`/events/${event._id}`} className="events-carousel-mobile-card-link">
                <div className="events-carousel-mobile-card-image-wrap">
                    <img
                        src={event?.image || '/default-event-image.jpg'}
                        alt={event?.title || 'Event'}
                        className="events-carousel-mobile-card-image"
                    />
                    <h3 className="events-carousel-mobile-card-title">{event?.title || 'Event'}</h3>
                </div>
                <div className="events-carousel-mobile-card-content">
                    <p className="events-carousel-mobile-card-date">
                        <FaCalendarAlt className="events-carousel-mobile-card-icon" aria-hidden="true" />
                        <span className="events-carousel-mobile-card-meta-text">
                            {formatEventDate(event)}{formattedTime ? `, ${formattedTime}` : ''}
                        </span>
                    </p>
                    <p className="events-carousel-mobile-card-location">
                        <FaMapMarkerAlt className="events-carousel-mobile-card-icon" aria-hidden="true" />
                        <span className="events-carousel-mobile-card-meta-text">
                            {event?.location?.address || 'Event location'}
                        </span>
                    </p>
                </div>
            </Link>
            <div className="events-carousel-mobile-card-actions">
                <button
                    className={`events-carousel-mobile-card-button${isGoing ? ' events-carousel-mobile-card-button--going' : ''}`}
                    onClick={handleAttendanceClick}
                    type="button"
                >
                    {isGoing ? 'Going' : 'Attend'}
                </button>
            </div>
        </article>
    );
};

const EventCarousel = ({ showTitle = false, mobileRail = false, title = 'Events Near You', railVariant = 'standard' }) => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); // To handle potential errors
    const [goingEventIds, setGoingEventIds] = useState(new Set());
    const [hasRailOverflow, setHasRailOverflow] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const mobileRailRef = useRef(null);
    const isCompactRail = railVariant === 'compact';
    const isSidebarCard = railVariant === 'sidebar-card';
    const currentProfileType = useSelector((state) => state.user.currentProfile);
    const user = useSelector((state) => state.user.user);
    const seller = useSelector((state) => state.seller.activeSeller);
    const hasEventAuth = Boolean(user?._id || user?.token || seller?._id || seller?.token);

    const getEventAuthHeaders = useCallback(() => {
        const headers = {
            'current-profile': currentProfileType,
        };

        if (user?.token) {
            headers.Authorization = `Bearer ${user.token}`;
        } else if (seller?.token) {
            headers.Authorization = `Bearer ${seller.token}`;
        }

        return headers;
    }, [currentProfileType, seller?.token, user?.token]);

    const updateMobileRailOverflow = useCallback(() => {
        const mobileRailElement = mobileRailRef.current;
        if (!mobileRailElement) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        const hasOverflow = mobileRailElement.scrollWidth > mobileRailElement.clientWidth + 2;

        setHasRailOverflow(hasOverflow);
        setCanScrollLeft(hasOverflow && mobileRailElement.scrollLeft > 2);
        setCanScrollRight(
            hasOverflow && mobileRailElement.scrollLeft + mobileRailElement.clientWidth < mobileRailElement.scrollWidth - 2
        );
    }, []);

    const scrollMobileRail = useCallback((direction) => {
        const mobileRailElement = mobileRailRef.current;
        if (!mobileRailElement) {
            return;
        }

        const scrollDistance = isCompactRail
            ? Math.max(mobileRailElement.clientWidth * 0.72, 180)
            : Math.max(mobileRailElement.clientWidth * 0.82, 220);
        mobileRailElement.scrollBy({
            left: direction === 'next' ? scrollDistance : -scrollDistance,
            behavior: 'smooth',
        });

        window.setTimeout(updateMobileRailOverflow, 360);
    }, [isCompactRail, updateMobileRailOverflow]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get(`${server}/event/events`);
                setEvents(response.data);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching events:', error);
                setError('Failed to load events.');
                setIsLoading(false);
            }
        };

        fetchEvents();
    }, []);

    useEffect(() => {
        if (!mobileRail || !hasEventAuth) {
            setGoingEventIds(new Set());
            return;
        }

        const fetchAttendingEvents = async () => {
            try {
                const response = await axios.get(`${server}/event/events/my`, {
                    withCredentials: true,
                    headers: getEventAuthHeaders(),
                    params: {
                        profile: currentProfileType,
                    },
                });
                const attendingEventIds = new Set((response.data || []).map((eventItem) => eventItem._id));
                setGoingEventIds(attendingEventIds);
            } catch (fetchError) {
                console.error('Error fetching attending events:', fetchError);
            }
        };

        fetchAttendingEvents();
    }, [currentProfileType, getEventAuthHeaders, hasEventAuth, mobileRail]);

    useEffect(() => {
        if (!mobileRail) {
            return undefined;
        }

        updateMobileRailOverflow();
        window.addEventListener('resize', updateMobileRailOverflow);

        return () => {
            window.removeEventListener('resize', updateMobileRailOverflow);
        };
    }, [events, mobileRail, updateMobileRailOverflow]);

    const handleCompactRailRSVP = useCallback(async (eventId) => {
        if (!hasEventAuth) {
            alert('Please log in to attend this event.');
            return;
        }

        try {
            await axios.post(`${server}/event/rsvp/${eventId}`, {
                currentProfile: currentProfileType,
            }, {
                withCredentials: true,
                headers: getEventAuthHeaders(),
            });
            setGoingEventIds((previousEventIds) => {
                const nextEventIds = new Set(previousEventIds);
                nextEventIds.add(eventId);
                return nextEventIds;
            });
        } catch (rsvpError) {
            console.error('Error RSVPing to event:', rsvpError);
            alert('Failed to RSVP: ' + (rsvpError.response?.data?.message || 'Error occurred'));
        }
    }, [currentProfileType, getEventAuthHeaders, hasEventAuth]);

    const handleCompactRailCancelRSVP = useCallback(async (eventId) => {
        if (!hasEventAuth) {
            alert('Please log in to update this event.');
            return;
        }

        try {
            await axios.delete(`${server}/event/rsvp/${eventId}`, {
                withCredentials: true,
                headers: getEventAuthHeaders(),
                data: {
                    currentProfile: currentProfileType,
                },
            });
            setGoingEventIds((previousEventIds) => {
                const nextEventIds = new Set(previousEventIds);
                nextEventIds.delete(eventId);
                return nextEventIds;
            });
        } catch (cancelError) {
            console.error('Failed to cancel RSVP:', cancelError);
            alert('Failed to cancel RSVP: ' + (cancelError.response?.data?.message || 'Error occurred'));
        }
    }, [currentProfileType, getEventAuthHeaders, hasEventAuth]);

    const renderEvents = () => {
        if (isSidebarCard) {
            const [event] = events;

            return (
                <div className="events-carousel-sidebar-card-shell">
                    <EventCard event={event} onRSVP={noop} onCancelRSVP={noop} />
                </div>
            );
        }

        if (mobileRail) {
            return (
                <div className="events-carousel-mobile-shell">
                    {hasRailOverflow && (
                        <button
                            type="button"
                            className="events-carousel-mobile-arrow events-carousel-mobile-arrow--prev"
                            onClick={() => scrollMobileRail('previous')}
                            disabled={!canScrollLeft}
                            aria-label="Previous event"
                        >
                            <FaChevronLeft aria-hidden="true" />
                        </button>
                    )}
                    <div
                        className="events-carousel-mobile-rail"
                        ref={mobileRailRef}
                        onScroll={updateMobileRailOverflow}
                    >
                        {events.map((event) => (
                            <EventMobileRailCard
                                key={event._id}
                                event={event}
                                isGoing={goingEventIds.has(event._id)}
                                onCancelRSVP={handleCompactRailCancelRSVP}
                                onRSVP={handleCompactRailRSVP}
                            />
                        ))}
                    </div>
                    {hasRailOverflow && (
                        <button
                            type="button"
                            className="events-carousel-mobile-arrow events-carousel-mobile-arrow--next"
                            onClick={() => scrollMobileRail('next')}
                            disabled={!canScrollRight}
                            aria-label="Next event"
                        >
                            <FaChevronRight aria-hidden="true" />
                        </button>
                    )}
                </div>
            );
        }

        return (
            <div className="events-carousel-body">
                <Carousel
                    indicators={false}
                    navButtonsAlwaysVisible={true}
                    autoPlay={false}
                    height="auto"
                >
                    {events.map((event) => (
                        <div key={event._id} className="events-carousel-slide">
                            <Link
                                to={`/events/${event._id}`}
                                className="events-carousel-link"
                            >
                                <EventCard event={event} onRSVP={noop} onCancelRSVP={noop} />
                            </Link>
                        </div>
                    ))}
                </Carousel>
            </div>
        );
    };

    const containerClasses = [
        'events-carousel-container',
        showTitle ? 'events-carousel-container--with-title' : '',
        mobileRail ? 'events-carousel-container--rail' : '',
        isCompactRail ? 'events-carousel-container--compact-rail' : '',
        isSidebarCard ? 'events-carousel-container--sidebar-card' : '',
    ].filter(Boolean).join(' ');

    return (
        <section className={containerClasses} aria-label={title}>
            {showTitle && (
                <div className="events-carousel-heading">
                    <FaCalendarAlt className="events-carousel-heading-icon" aria-hidden="true" />
                    <h2 className="events-carousel-heading-title">{title}</h2>
                </div>
            )}
            {isLoading ? (
                <div className="events-carousel-loading">Loading...</div>
            ) : error ? (
                <div className="events-carousel-loading">{error}</div>
            ) : (
                events.length > 0 ? ( 
                    renderEvents()
                ) : (
                    // Render the fallback prompt when there are no events
                    <CreateEventPrompt />
                )
            )}
        </section>
    );
};

export default EventCarousel;




